import { FloodgateStorageProviderFileType as FileType } from "@ms-ofb/floodgate-shared-typings";
import { getDynamicSetting, SettingKey } from "@ms-ofb/officepersonalization/dist/lib/Configuration/DynamicConfiguration";
import FloodgateStorageProvider from "../FloodgateCore/FloodgateStorageProvider";
import * as Configuration from "./../Configuration/Configuration";
import { IInitOptionsCommon, ISamplingInitOption } from "./../Configuration/IInitOptions";
import { Environment } from "./../Constants";
import * as Utils from "./../Utils";
import { ICustomProperties, IEventId } from "./Constants";
import { App, Browser, Device, Host, Release, Session } from "./Telemetry/Contracts";
import ITelemetryLogger from "./Telemetry/ITelemetryLogger";
import TelemetryLoggerFactory from "./Telemetry/TelemetryLoggerFactory";

export { EventIds, ICustomProperties, IEventId } from "./Constants";

const { isNOU } = Utils;
const FEEDBACK_NAMESPACE = "Office.Feedback";
const ENGAGEMENT_NAMESPACE = "Office.Engagement";

let logger: ILogger;
let userFactLogger: IUserFactLogger;
let env: Environment;

/**
 * Initialize the logging module
 * @param initOptionsCommon object contains common init options
 * @param appName app name
 * @param appVersion app version
 * @param logSessionId local SDK session id
 */
export function initialize(initOptionsCommon: IInitOptionsCommon, appName: string, appVersion: string,
	logSessionId: string, samplingOption: ISamplingInitOption): void {

	const telemetryGroup = initOptionsCommon.telemetryGroup ? initOptionsCommon.telemetryGroup : {};
	const webGroup = initOptionsCommon.webGroup ? initOptionsCommon.webGroup : {};

	logger = new Logger(
		TelemetryLoggerFactory.create(
			TokenManager.getTenantToken(initOptionsCommon.environment),
			FEEDBACK_NAMESPACE,
			initOptionsCommon.telemetrySink,
			new App(appName, appVersion),
			new Session(logSessionId),
			new Host(initOptionsCommon.appId.toString(), initOptionsCommon.originalLocale, initOptionsCommon.sessionId, initOptionsCommon.build),
			new Release(telemetryGroup.audienceGroup),
			new Browser(webGroup.browser, webGroup.browserVersion),
			new Device(telemetryGroup.deviceId),
		),
		samplingOption,
		initOptionsCommon
	);

	userFactLogger = new UserFactLogger(
		TelemetryLoggerFactory.create(
			TokenManager.getTenantTokenByNamespace(ENGAGEMENT_NAMESPACE),
			ENGAGEMENT_NAMESPACE,
			initOptionsCommon.telemetrySink,
			new App(appName, appVersion),
			new Session(logSessionId),
			new Host(initOptionsCommon.appId.toString(), initOptionsCommon.originalLocale, initOptionsCommon.sessionId, initOptionsCommon.build),
			new Release(telemetryGroup.audienceGroup),
			new Browser(webGroup.browser, webGroup.browserVersion),
			new Device(telemetryGroup.deviceId),
		)
	);

	env = initOptionsCommon.environment;
}

/**
 * Get the logger object
 */
export function getLogger(): ILogger {
	return logger;
}

/**
 * Get the user fact logger object
 */
export function getUserFactLogger(): IUserFactLogger {
	return userFactLogger;
}

/**
 * Interface for a logger
 */
export interface ILogger {
	logEvent(eventId: IEventId, logSeverity: LogLevel, customProperties?: ICustomProperties, internalSampleRate?: number): void;
	logLocal?(logSeverity: LogLevel, ...others: any[]): void;
	flushQueue?(): void;
}

export interface IUserFactLogger {
	logUserFact(userFact: string, factValue: string): void;
}

/**
 * Log level settings
 */
export const enum LogLevel {
	None,
	Critical,
	Error,
	Info
}

interface ILogSettings {
	consoleLog?: boolean;
	isEnabled?: boolean;
	logLevel?: LogLevel;
}

interface ILogLevelSettings {
	content: ILogSettings;
}

/**
 * Class representing a logger for the feedback SDK
 */
export class Logger implements ILogger {
	private static EVENT_NAME = "SDK";
	private static EVENT_ID = "EventId";
	private static DEFAULT_SAMPLE_RATE = 1;
	private static IS_DYNAMIC_UX_ENABLED = "IsDynamicUXEnabled";
	private static IS_DYNAMIC_CAMPAIGN_ENABLED = "IsDynamicCampaignEnabled";

	private telemetryLogger: ITelemetryLogger;
	private logLevel: LogLevel;
	private isConsoleLogEnabled: boolean;
	private dynamicLogSettings: ILogLevelSettings;

	private eventSampling: ISamplingInitOption;
	private initOptionsCommon: IInitOptionsCommon;

	private readonly SAMPLING_RESULT = Math.random();

	constructor(telemetryLogger: ITelemetryLogger, samplingOptions: ISamplingInitOption, initOptionsCommon?: IInitOptionsCommon) {
		if (!telemetryLogger) {
			throw new Error("telemetryLogger must not be null");
		}

		this.telemetryLogger = telemetryLogger;
		this.eventSampling = samplingOptions;
		this.initOptionsCommon = initOptionsCommon;
	}

	/**
	 * Flush the queue and send all events at once.
	 */
	public flushQueue(): void {
		this.telemetryLogger.flushQueue();
	}

	/**
	 * Log an event
	 * @param eventId event Id
	 * @param logSeverity the log level severity for the message
	 * @param customProperties custom properties to add to the log
	 * @param internalSampleRate sample rate for this event in decimal (example: 0.05 for 5%). (Does not override host set sampleRate)
	 */
	public logEvent(eventId: IEventId, logSeverity: LogLevel, customProperties?: ICustomProperties, internalSampleRate?: number): boolean {
		if (!eventId) {
			throw new Error("eventId must not be null");
		}

		let sampleRate: number;

		// Host set sample rate has precedence over internalSampleRate.
		if (this.isEventSampled(eventId.name)) {
			sampleRate = this.getEventSamplingRate(eventId.name);
		} else if (internalSampleRate !== undefined) {
			sampleRate = internalSampleRate;
		}

		if (isNOU(logSeverity) || logSeverity === LogLevel.None) {
			if (env === Environment.Production) {
				const errorMessage = "logSeverity must not be null or none";
				if (this.isConsoleLogEnabled && console) {
					console.log("Floodgate event: ", Logger.EVENT_NAME, errorMessage);
				}
				this.telemetryLogger.logEvent(Logger.EVENT_NAME, { ErrorMessage: errorMessage }, sampleRate);
			}
			return false;
		}

		// add base values for every log event
		customProperties = customProperties || {};
		(<any>customProperties)[Logger.EVENT_ID] = eventId.name;
		(<any>customProperties)[Logger.IS_DYNAMIC_UX_ENABLED] = Configuration.get().getFloodgateInitOptions()?.dynamicUxConfig?.enabled ?? false;
		(<any>customProperties)[Logger.IS_DYNAMIC_CAMPAIGN_ENABLED] = Configuration.get().getFloodgateInitOptions()?.dynamicCampaignConfig?.enabled ?? false;

		if (this.isLoggingEnabled(logSeverity, sampleRate) || this.isEventTelemetryEnabled(eventId.name)) {
			sampleRate = sampleRate ?? Logger.DEFAULT_SAMPLE_RATE;
			this.telemetryLogger.logEvent(Logger.EVENT_NAME, customProperties, sampleRate);
		}

		if (this.isConsoleLogEnabled && console) {
			console.log("Floodgate event: ", Logger.EVENT_NAME, logSeverity, customProperties);
		}

		return true;
	}

	public logLocal?(logSeverity: LogLevel, ...others: any[]): void {
		if (this.isLoggingEnabled(logSeverity) && this.isConsoleLogEnabled && console) {
			console.log("LPP FG: ", logSeverity, others);
		}
	}

	public getUserSamplingRate(): number {
		return this.SAMPLING_RESULT;
	}

	public isEventTelemetryEnabled(eventName: string): boolean {
		try {
			if (!this.dynamicLogSettings) {
				return false;
			}

			const appId = this.initOptionsCommon?.appId?.toString();
			const locale = this.initOptionsCommon?.originalLocale?.toLowerCase();
			const settingName = `content_${eventName}`;

			// try settings from more specific to generic order
			const overrideSettings =
				this.dynamicLogSettings[`${settingName}_${appId}_${locale}` as unknown as keyof ILogLevelSettings] ||
				this.dynamicLogSettings[`${settingName}_${appId}` as unknown as keyof ILogLevelSettings] ||
				this.dynamicLogSettings[`${settingName}_${locale}` as unknown as keyof ILogLevelSettings] ||
				this.dynamicLogSettings[settingName as keyof ILogLevelSettings];

			return overrideSettings?.isEnabled;
		} catch (e) {
			if (this.isConsoleLogEnabled) {
				console.log("FG: Error checking for dynamic log settings: ", e);
			}
		}

		return false;
	}

	/**
	 * This method enables logging if either the log severity of the event is less than or equal to the current log level set or if the event
	 * has an inputSampleRate and the current log level is not set to None, and if the sample rate is greater than 0 and the random generated
	 * number is within the sample rate. 
	 * 
	 * For example, if the log severity of the event is LogLevel.Error and the current log level is set as LogLevel.Critical, then
	 * isLoggingEnabled will return false due to the condition LogLevel.Error <= LogLevel.Critical unless the event has an inputSampleRate. 
	 * If the event meets either of these conditions, then a random number will be generated. If the random generated number is less than
	 * the sample rate and the sample rate itself is not full blocking (0 value), the event will be logged. 
	 * 
	 * If no current log level is set, then log level is set as LogLevel.Error by default. In this case, all events with critical log
	 * severity, events with error log severity, and events with a given sample rate get logged. If the current log level is None, no
	 * events are logged.
	 * 
	 * @param inputLogLevel the log severity of the event. This follows the order defined in the LogLevel enum (None < Critical < Error < Info)
	 * @param inputSampleRate the percentage of users allowed to send the event back (given either through the host or the internalSampleRate)
	 */
	private isLoggingEnabled(inputLogLevel: LogLevel, inputSampleRate?: number): boolean {
		if (isNOU(this.logLevel)) {
			const currentLogLevel = this.getCurrentLogLevel();
			this.logLevel = isNOU(currentLogLevel) ? LogLevel.Error : currentLogLevel;
		}

		const sampleRate = inputSampleRate ?? Logger.DEFAULT_SAMPLE_RATE;

		return ((inputLogLevel <= this.logLevel || (this.logLevel !== LogLevel.None && inputSampleRate !== undefined))
			&& this.SAMPLING_RESULT <= sampleRate && sampleRate !== 0);
	}

	/**
	 * This method returns the current log level if it is set in either url query parameters or floodgate local storage.
	 * Following table summarizes whether the log event calls with a log severity will log the events or not according
	 * to the current log level set. For example, the first row in the table demonstrates that when the current log level
	 * enabled is critical, then the log event will log the events only if the log severity is critical.
	 *
	 * Current Log Level Enabled | LogSeverity: Critical	| LogSeverity: Error	| LogSeverity: Info
	 * --------------------------------------------------------------------------------------------
	 * Critical Enabled			 | yes      				| no    			 	| no
	 * Error Enabled       		 | yes      				| yes   			 	| no
	 * Info Enabled        		 | yes      				| yes   			 	| yes
	 * None Enabled        		 | no       				| no    			 	| no
	 *
	 * Console logging will be enabled when the current log level is set through the url query parameters or
	 * when the url query parameter "obfconsolelog" is explicitly set to true.
	 */
	private getCurrentLogLevel(): LogLevel {
		try {
			// Check if console log is enabled in local browser session with query parameter overrides
			const urlParams = typeof URLSearchParams !== "undefined" && new URLSearchParams(window.location.search) || undefined;
			let consoleLogParam: string | boolean = urlParams && urlParams.get("obfconsolelog");
			if (!consoleLogParam) {
				// for getting iframe host url - eg: WAC
				const documentReferrer = typeof document !== "undefined" && document.referrer;
				consoleLogParam = documentReferrer && documentReferrer.indexOf("obfconsolelog") !== -1;
			}

			if (consoleLogParam) {
				this.isConsoleLogEnabled = true;
			}

			// Check if log level is set in local browser session with query parameter overrides
			const logLevelParam = urlParams && urlParams.get("obfloglevel");
			if (!isNOU(logLevelParam)) {
				this.isConsoleLogEnabled = true;
				return parseInt(logLevelParam, 10);
			}

			// Check if log level is set in floodgate local storage (manually or with remotely fetched settings)
			if (FloodgateStorageProvider.isStorageAvailable()) {
				const floodgateStorageProvider: FloodgateStorageProvider = new FloodgateStorageProvider();
				let tempStorageItem: string = floodgateStorageProvider.read(FileType.LogLevelSettings);
				if (!tempStorageItem) {
					tempStorageItem = getDynamicSetting(SettingKey.logLevelSettings, "");
				}

				const settingsObj = tempStorageItem && this.parseObject(tempStorageItem);
				if (settingsObj) {
					this.dynamicLogSettings = settingsObj;
					let logSettings = settingsObj.content as ILogSettings;

					if (this.initOptionsCommon) {
						const appId = this.initOptionsCommon.appId && this.initOptionsCommon.appId.toString();
						const locale = this.initOptionsCommon.originalLocale && this.initOptionsCommon.originalLocale.toLowerCase();

						// log settings from more specific to generic order
						logSettings = settingsObj[`content_${appId}_${locale}`] ||
							settingsObj[`content_${appId}`] ||
							settingsObj[`content_${locale}`] ||
							settingsObj[`content`];
					}

					if (logSettings) {
						if (logSettings.consoleLog) {
							this.isConsoleLogEnabled = true;
						}

						return logSettings.logLevel;
					}
				}
			}
		} catch (e) {
			if (this.isConsoleLogEnabled) {
				console.log("Error while getting the current log level: ", e);
			}
			return undefined;
		}

		return null;
	}

	private parseObject(tempItem: string): any {
		try {
			return JSON.parse(tempItem);
		} catch (e) {
			if (this.isConsoleLogEnabled) {
				console.log("Error while parsing the json string for log level: ", e);
			}
			return null;
		}
	}

	private getEventSamplingRate(eventId: string): number {
		let sampleRate = 1;
		if (this.eventSampling.event) {
			// If the event is init with sample rate
			// check the sampling result against the sample rate
			if (eventId in this.eventSampling.event) {
				sampleRate = this.eventSampling.event[eventId];
			}
		}
		return sampleRate;
	}

	private isEventSampled(eventId: string): boolean {
		if (this.eventSampling.event) {
			if (eventId in this.eventSampling.event) {
				return true;
			}
		}
		return false;
	}
}

export class UserFactLogger implements IUserFactLogger {
	private static FACTVALUE_DATA_PROPERTY = "FactValue";
	private telemetryLogger: ITelemetryLogger;
	constructor(telemetryLogger: ITelemetryLogger) {
		if (!telemetryLogger) {
			throw new Error("telemetryLogger must not be null");
		}
		this.telemetryLogger = telemetryLogger;
	}

	/**
	 * Log a user fact
	 * @param userFactName user fact name
	 * @param factTime time the user fact occured
	 */
	public logUserFact(userFactName: string, factValue: string): void {
		if (!userFactName) {
			throw new Error("user fact must not be null");
		}
		if (!factValue) {
			throw new Error("fact value must not be null");
		}

		const customProperties: ICustomProperties = { [UserFactLogger.FACTVALUE_DATA_PROPERTY]: factValue };
		this.telemetryLogger.logEvent(userFactName, customProperties);

		// tslint:disable:no-console
		getLogger().logLocal(LogLevel.Info, userFactName, customProperties);
	}
}

class TokenManager {
	public static getTenantToken(environment: Environment): string {
		if (environment === Environment.Production) {
			return TokenManager.TENANT_TOKEN_FEEDBACK_PRODUCTION;
		} else {
			return TokenManager.TENANT_TOKEN_FEEDBACK_PRE_PRODUCTION;
		}
	}

	public static getTenantTokenByNamespace(namespace: string): string {
		if (namespace === ENGAGEMENT_NAMESPACE) {
			return TokenManager.TENANT_TOKEN_ENGAGEMENT;
		}
		return null;
	}
	private static TENANT_TOKEN_ENGAGEMENT =
		"ceda5b13e30f4f9da3617f64bfcb9a6b-7d7a3ec0-00b0-4642-9986-18cef650aa81-7263"; // "Office Engagement" Prod Aria tenant
	private static TENANT_TOKEN_FEEDBACK_PRODUCTION =
		"d79e824386c4441cb8c1d4ae15690526-bd443309-5494-444a-aba9-0af9eef99f84-7360"; // "Office Feedback" Prod Aria tenant
	private static TENANT_TOKEN_FEEDBACK_PRE_PRODUCTION =
		"2bf6a2ffddca4a80a892a0b182132961-625cb102-8b0c-480e-af53-92e48695d08d-7736"; // "Office Feedback" Sandbox Aria tenant
}
